import { useTranslation } from 'react-i18next'
import { House, Languages, RotateCw, UserRound } from 'lucide-react'
import { useAccessibilitySession } from '../../../accessibility-session'
import { LanguageTiles } from '../../../components/LanguageTiles'
import { Tile, type TileTone } from '../../../components/Tile'
import { KioskStep } from '../../../../../components/kiosk-step'
import { KioskLoadingPanel, KioskStatePanel } from '../../../../../components/kiosk-state-panel'
import { useSchedulerExitToStart, useSchedulerSelector, useSchedulerSend } from '../context'

/** Tile tones cycled across authored answers, matching the appointment-type step. */
const ANSWER_TONES: TileTone[] = ['sky-subtle', 'orange-subtle', 'blue-subtle', 'success-subtle']

/** Display authored questions without playing unrelated static question audio.
 * Answer tiles are content-sized and centred instead of stretched to the
 * viewport (KO-15); the unavailable-language state explains itself and offers
 * the languages plus reception help (KO-38); every state has a header title
 * (KO-12) and reception handoffs offer a way home.
 * @since 1.1.8
 * @example <QuestionnaireStep />
 */
export function QuestionnaireStep() {
  const { t } = useTranslation()
  const response = useSchedulerSelector((s) => s.context.questionnaire)
  const send = useSchedulerSend()
  const exitToStart = useSchedulerExitToStart()
  const { setSessionLanguage } = useAccessibilitySession()
  if (response?.kind === 'question') return (
    <KioskStep
      width="wide"
      title={response.text}
      lead={response.help === undefined ? undefined : <span className="whitespace-pre-wrap" dir="auto">{response.help}</span>}
    >
      <div className="grid grid-cols-1 content-start gap-4 md:grid-cols-2">
        {response.answers.map((answer, index) => (
          <Tile
            key={answer.answerId}
            tone={ANSWER_TONES[index % ANSWER_TONES.length]}
            className="min-h-32"
            data-testid={`scheduler-questionnaire-${answer.answerId}-button`}
            name={<span dir="auto">{answer.text}</span>}
            onClick={() => send({ type: 'QUESTIONNAIRE_ANSWER', answerId: answer.answerId })}
          />
        ))}
      </div>
    </KioskStep>
  )
  if (response?.kind === 'unavailable') return (
    <KioskStep
      width="default"
      title={t('self-service.language-entry.title')}
      lead={t('self-service.scheduler.questionnaire.language-unavailable-lead')}
    >
      <KioskStatePanel
        tone="info"
        icon={<Languages />}
        title={t('self-service.scheduler.questionnaire.language-unavailable-title')}
        body={t('self-service.scheduler.questionnaire.language-unavailable-body')}
      />
      <LanguageTiles
        codes={response.availableLanguages}
        testIdPrefix="scheduler-questionnaire-language"
        onSelect={(code) => { setSessionLanguage(code); send({ type: 'QUESTIONNAIRE_RETRY' }) }}
      />
      <Tile
        tone="orange-subtle"
        size="band"
        className="min-h-20"
        data-testid="scheduler-questionnaire-reception-button"
        name={t('self-service.help-reception')}
        desc={t('self-service.help-reception-toast')}
        icon={<UserRound />}
        onClick={() => send({ type: 'QUESTIONNAIRE_ASSISTANCE' })}
      />
    </KioskStep>
  )
  if (response?.kind === 'reception') return (
    <KioskStep width="narrow" align="center" title={t('self-service.help-reception')}>
      <KioskStatePanel
        tone="info"
        icon={<UserRound />}
        title={t('self-service.help-reception')}
        body={<span className="whitespace-pre-wrap" dir="auto">{response.text || t('self-service.scheduler.questionnaire.reception-fallback')}</span>}
        bodyTestId="scheduler-questionnaire-reception-display"
        action={{
          label: t('self-service.scheduler.done.back-to-start'),
          icon: House,
          onClick: exitToStart,
          testId: 'scheduler-questionnaire-home-button',
        }}
      />
    </KioskStep>
  )
  return <QuestionnaireErrorStep />
}
/** Keep context and route loading visibly separate from disabled configuration.
 * @since 1.1.8
 * @example <QuestionnaireLoadingStep />
 */
export function QuestionnaireLoadingStep() {
  const { t } = useTranslation()
  return (
    <KioskStep width="narrow" align="center" title={t('self-service.scheduler.start.title')}>
      <KioskLoadingPanel title={t('self-service.scheduler.questionnaire.loading')} />
    </KioskStep>
  )
}
/** Failed authority reads offer retry or reception assistance without bypass.
 * @since 1.1.8
 * @example <QuestionnaireErrorStep />
 */
export function QuestionnaireErrorStep() {
  const { t } = useTranslation()
  const send = useSchedulerSend()
  const exitToStart = useSchedulerExitToStart()
  return (
    <KioskStep width="narrow" align="center" title={t('self-service.scheduler.start.title')}>
      <KioskStatePanel
        tone="error"
        title={t('self-service.scheduler.questionnaire.error-title')}
        body={t('self-service.scheduler.error.info')}
        bodyTestId="scheduler-questionnaire-error-display"
        action={{
          label: t('common.retry'),
          icon: RotateCw,
          onClick: () => send({ type: 'QUESTIONNAIRE_RETRY' }),
          testId: 'scheduler-questionnaire-retry-button',
        }}
        secondaryAction={{
          label: t('self-service.scheduler.done.back-to-start'),
          icon: House,
          onClick: exitToStart,
          testId: 'scheduler-questionnaire-error-home-button',
        }}
      />
    </KioskStep>
  )
}
